import { useCallback, useEffect, useState } from 'react';

import { concat, defaultsDeep, find, get, intersection, isArray, isEqual, isNumber, map, omit, size } from 'lodash';

import { FetchPagePayload } from '../actions/fetchPage';
import { DataPaginated } from '../reducers/createResultReducer';
import { OptionalResource } from '../resources/createResource';
import { ReturnTypeFetchPage } from '../resources/createResourceCommonActions';
import { EntityBase } from '../resources/types';

import useQuery, { UseQueryOptions, UseQueryResponse } from './useQuery';

export interface UsePaginationResponse<E extends EntityBase>
  extends Omit<UseQueryResponse<E>, 'response'>,
    Omit<DataPaginated<E>, 'response'> {
  page: number;
  items: E[];
  itemsAgregated: E[];
  totalSize?: number;
  hasNextPage: boolean;
  fetchMoreItems: () => void;
  payload: OptionalResource<FetchPagePayload<E>>;
}

/**
 * Hook for `resource.fetchPage` actions. Based on:
 *
 * - https://www.apollographql.com/docs/react/api/react-hooks/#usequery
 * - https://www.apollographql.com/docs/react/data/pagination/
 * - https://github.com/relay-tools/relay-hooks/blob/master/usePagination.md
 *
 * @param action the object returned by calling `resource.fetchPage`
 * @param options https://www.apollographql.com/docs/react/api/react-hooks/#options
 *
 * @example
 *
 * import { usePagination } from 'lib/hooks';
 * import { patientsResource } from 'lib/resources';
 *
 * const { data, loading, error } = usePagination(patientsResource.fetchPage({
 *   params: { itemsAgregated: 1, limit: 100 },
 * }));
 */
function usePagination<E extends EntityBase>(
  action: ReturnTypeFetchPage<E>,
  options: UseQueryOptions = {},
): UsePaginationResponse<E> {
  const [payload, setPayload] = useState<OptionalResource<FetchPagePayload<E>>>(
    defaultsDeep({}, action.payload, { params: { page: 1 } }),
  );
  const [prevPage, setPrevPage] = useState<number>();
  const [items, setItems] = useState<E[]>([]);
  const [itemsAgregated, setItemsAgregated] = useState<E[]>([]);
  const [totalSize, setTotalSize] = useState<number>();

  const result = useQuery({ type: '@lib/fetchPage/REQUEST', payload } as ReturnTypeFetchPage<E>, options);

  const data = result && result.data;
  const newParams = omit(get(action, 'payload.params'), ['page']);
  const oldParams = omit(payload.params as any, ['page']);
  const page = payload.params.page as number;
  const limit = payload.params.limit;

  useEffect(() => {
    // Reset and fetch first page in case other params have changed
    if (!isEqual(newParams, oldParams)) {
      setItems([]);
      setItemsAgregated([]);
      setTotalSize(undefined);
      setPayload(defaultsDeep({}, { params: { page: 1 } }, action.payload));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newParams, oldParams]);

  useEffect(() => {
    // WORKAROUND: handle paginated data comming as array instead of object for `activitiesResource.fetchPage`
    const newItems = get(data, 'response', data) as E[];
    if (!newItems || isEqual(items, newItems)) {
      return;
    }

    // Handle cases for 1 single page
    if (page === 1) {
      setTotalSize(newItems.length < limit ? newItems.length : undefined);
      setItems(newItems);
      setItemsAgregated(newItems);
      return;
    }

    // Check if we're receiving an update of current items
    const newIds = map(newItems, 'id');
    if (size(newItems) > 0 && intersection(map(items, 'id'), newIds).length === newIds.length) {
      // NOTE: New items are already included on itemsAgregated -> extend values in case there are new lookups
      setItems(newItems);
      setItemsAgregated(
        map(itemsAgregated, (item: E) => {
          const newItem = find(newItems, ({ id }) => id === item.id) as E;
          return newItem || item;
        }),
      );
      return;
    }

    // Update items agregated, useful for scenarios with inifinite scroll
    let newItemsAgregated: E[];
    if (page && prevPage && page === prevPage + 1) {
      newItemsAgregated = concat(itemsAgregated, newItems);
    } else if (page > 1 && page === prevPage) {
      newItemsAgregated = itemsAgregated;
    } else {
      newItemsAgregated = newItems;
    }

    // Guess `totalSize` in case the API doesn't provide it
    if (!isNumber(totalSize)) {
      let newTotalSize = data && data.totalSize;
      if (!isNumber(newTotalSize) && newItems.length < limit) {
        newTotalSize = newItemsAgregated.length;
      }
      setTotalSize(newTotalSize);
    }

    // Update the state with items refreshed
    setItems(newItems);
    setItemsAgregated(newItemsAgregated);
  // eslint-disable-next-line
  }, [page, prevPage, limit, data, itemsAgregated, items, setItems, setItemsAgregated, totalSize]);

  const fetchMoreItems = useCallback(() => {
    setPrevPage(page);
    setPayload(defaultsDeep({}, { params: { page: page + 1 } }, payload));
  }, [page, setPrevPage, payload, setPayload]);

  return {
    ...omit(result, ['response', 'data']),
    ...(isArray(data) ? { size: size(items) } : omit(data, ['response'])),
    page,
    items,
    itemsAgregated,
    totalSize,
    // NOTE: Returning `totalSize: true` until we know the real value
    hasNextPage: isNumber(totalSize) ? totalSize > itemsAgregated.length : true,
    fetchMoreItems,
    payload,
  };
}

export default usePagination;
